import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate } from 'react-router'
import { MagnifyingGlassIcon, PlusIcon } from '@phosphor-icons/react'
import { Tribe } from '../../types/Tribe'
import { User } from '../../types/User'
import {
  useCreateTribeMutation,
  useLazyGetAllTribesQuery,
} from '../../redux/tribe.endpoints'
import { toast } from '../../utils/toast'
import { TribeModal } from '../../components/TribeModal/TribeModal'
import TribesTable from '../../components/TribesTable/TribesTable'
import Loader from '../../components/Loader/Loader'

interface Props {
  user: User
}

const TribesView = ({ user }: Props) => {
  const navigate = useNavigate()
  const [tribes, setTribes] = useState<Tribe[]>([])
  const [searchValue, setSearchValue] = useState('')
  const [isModalOpen, setModalOpen] = useState(false)

  const [getTribes, { data, isLoading }] = useLazyGetAllTribesQuery()
  const [createTribe] = useCreateTribeMutation()

  useEffect(() => {
    getTribes({
      companyId: user.company.id,
      pageSize: 0,
      pageNumber: 0,
      sortBy: 'id',
      searchQuery: '',
      sortType: 'DESC',
    })
  }, [getTribes, user.company.id])

  useEffect(() => {
    setTribes(data || [])
  }, [data])

  const visibleTribes = useMemo(
    () =>
      tribes.filter(tribe =>
        tribe.name.toLowerCase().includes(searchValue.toLowerCase()),
      ),
    [tribes, searchValue],
  )

  const handleSaveTribe = async (name: string) => {
    try {
      await createTribe({
        name,
        isActive: true,
        description: '',
        companyId: user.company.id,
        isDeliveryTribe: false,
      }).unwrap()
      getTribes({
        companyId: user.company.id,
        pageSize: 0,
        pageNumber: 0,
        sortBy: 'id',
        searchQuery: '',
        sortType: 'DESC',
      })
      setModalOpen(false)
    } catch (error: any) {
      toast.error(
        `An error occurred when creating the tribe: ${
          error?.data?.error ?? 'Unknown error'
        }`,
        { toastId: 'create-tribe-error' },
      )
      return false
    }
  }

  const openTribe = (tribe: Tribe) => navigate(`/tribes/${tribe.id}`)

  return (
    <div className="container my-4 manage-tribes">
      <Helmet title="Time Off - Tribes" />

      <div className="manage-tribes__header">
        <h1 className="manage-tribes__title">Manage Tribes</h1>
        <button
          type="button"
          className="manage-tribes__add-btn"
          onClick={() => setModalOpen(true)}
        >
          <PlusIcon size={16} weight="regular" color="#ffffff" />
          Add New Tribe
        </button>
      </div>

      <div className="manage-tribes__filters">
        <div className="manage-tribes__search">
          <input
            type="text"
            className="manage-tribes__search-input"
            placeholder="Search by Tribe"
            aria-label="Search by Tribe"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
          />
          <MagnifyingGlassIcon
            size={16}
            weight="regular"
            className="manage-tribes__search-icon"
          />
        </div>
      </div>

      {isLoading ? <Loader /> : (<TribesTable
        tribes={visibleTribes}
        onRowClick={openTribe}
        onEdit={openTribe}
      />)}

      <TribeModal
        show={isModalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSaveTribe}
      />
    </div>
  )
}

export default TribesView
